/**
 * InterviewScorecardPublic — magic-link page where an interviewer fills
 * their scorecard. NO auth required: the URL token is the credential.
 *
 * Route: /scorecard/:token
 *
 * Flow:
 *   1. Decode token via GET /api/public/interview-scorecard/:token
 *   2. Render context (candidate, round, when) + scorecard template
 *      questions (rating_5, yes_no, text, single_select, multi_select,
 *      recommendation).
 *   3. POST /api/public/interview-scorecard/:token/submit
 *
 * Blind mode is enforced server-side — this page never shows other
 * interviewers' scorecards.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CheckCircle, Star, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/interview-scorecard`;

const REC_LABEL = {
  strong_hire: "Strong Hire",
  hire: "Hire",
  no_hire: "No Hire",
  strong_no_hire: "Strong No Hire",
};
const REC_COLOR = {
  strong_hire: "bg-emerald-600 hover:bg-emerald-700 text-white",
  hire: "bg-emerald-100 text-emerald-900 hover:bg-emerald-200",
  no_hire: "bg-rose-100 text-rose-900 hover:bg-rose-200",
  strong_no_hire: "bg-rose-600 text-white hover:bg-rose-700",
};

function Stars({ value, onChange, max = 5 }) {
  return (
    <div className="flex gap-1">
      {Array.from({ length: max }).map((_, i) => {
        const v = i + 1;
        return (
          <button key={v} type="button" onClick={() => onChange(v)} className="p-0.5" aria-label={`${v} star${v > 1 ? "s" : ""}`}>
            <Star size={24} weight={value >= v ? "fill" : "regular"} className={value >= v ? "text-amber-500" : "text-zinc-300 hover:text-amber-400"} />
          </button>
        );
      })}
    </div>
  );
}

export default function InterviewScorecardPublic() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true });
  const [answers, setAnswers] = useState({});
  const [rec, setRec] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/${token}`);
        setState({ loading: false, data: r.data });
        if (r.data?.already_submitted) setSubmitted(true);
      } catch (e) {
        setState({ loading: false, error: e?.response?.data?.detail || "Couldn't load scorecard" });
      }
    })();
  }, [token]);

  if (state.loading) {
    return <ScorecardShell><div className="text-center text-zinc-500 py-12">Loading…</div></ScorecardShell>;
  }
  if (state.error) {
    return (
      <ScorecardShell>
        <Card className="p-8 text-center">
          <Warning size={32} weight="bold" className="mx-auto text-rose-600 mb-2" />
          <div className="font-semibold text-zinc-900">{state.error}</div>
          <div className="text-xs text-zinc-500 mt-1">Ask the hiring team to re-send your invite.</div>
        </Card>
      </ScorecardShell>
    );
  }

  const { interview, interviewer, template, rec_values } = state.data;

  if (submitted) {
    return (
      <ScorecardShell>
        <Card className="p-8 text-center">
          <CheckCircle size={36} weight="fill" className="mx-auto text-emerald-600 mb-2" />
          <div className="text-lg font-semibold text-zinc-900">Scorecard submitted</div>
          <div className="text-sm text-zinc-600 mt-1">
            Thank you, {interviewer.name || interviewer.email}. Your feedback is now locked and shared with the panel once everyone has submitted.
          </div>
        </Card>
      </ScorecardShell>
    );
  }

  const setAnswer = (qid, v) => setAnswers((a) => ({ ...a, [qid]: v }));
  const validate = () => {
    for (const q of template.questions || []) {
      if (q.type === "recommendation") continue;
      if (!q.required) continue;
      const v = answers[q.id];
      if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0)) {
        toast.error(`Required: ${q.label}`); return false;
      }
    }
    if (!rec) { toast.error("Please pick an overall recommendation"); return false; }
    return true;
  };

  const submit = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/${token}/submit`, {
        answers, overall_recommendation: rec, notes: notes || null,
      });
      toast.success("Scorecard submitted");
      setSubmitted(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit");
    } finally { setBusy(false); }
  };

  const fmtWhen = (() => { try { return new Date(interview.scheduled_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }); } catch { return interview.scheduled_at; } })();

  return (
    <ScorecardShell>
      <Card className="p-6 space-y-1">
        <div className="text-[11px] uppercase tracking-wider text-indigo-600 font-semibold">Interview scorecard</div>
        <h1 className="text-2xl font-bold text-zinc-900">{interview.candidate_name}</h1>
        <div className="text-sm text-zinc-600">{interview.requisition_title} · {interview.round_name}</div>
        <div className="text-xs text-zinc-500 mt-1">
          {fmtWhen} · {interview.duration_min}m · {interview.mode === "video" ? "Video call" : "In person"}
        </div>
        {interview.meeting_link && (
          <a href={interview.meeting_link} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 hover:underline break-all">{interview.meeting_link}</a>
        )}
        {interview.location && (
          <div className="text-xs text-zinc-600">📍 {interview.location}</div>
        )}
        {interview.agenda && (
          <Card className="p-2 mt-2 bg-indigo-50 border-indigo-100">
            <div className="text-[10px] uppercase tracking-wider text-indigo-700 font-semibold">Agenda</div>
            <div className="text-xs text-indigo-900 mt-0.5 whitespace-pre-line">{interview.agenda}</div>
          </Card>
        )}
        <div className="text-[11px] text-zinc-500 mt-2">
          Hi {(interviewer.name || "").split(" ")[0] || interviewer.email} — your feedback stays private until every interviewer in this round submits.
        </div>
      </Card>

      <Card className="p-6 space-y-5">
        <div>
          <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">{template.name}</div>
          {template.description && <div className="text-xs text-zinc-500 italic mt-0.5">{template.description}</div>}
        </div>

        {(template.questions || []).filter((q) => q.type !== "recommendation").map((q) => (
          <QuestionField key={q.id} q={q} value={answers[q.id]} onChange={(v) => setAnswer(q.id, v)} />
        ))}

        <div className="border-t border-zinc-200 pt-4">
          <Label className="text-sm font-semibold">Overall recommendation <span className="text-rose-600">*</span></Label>
          <div className="flex gap-2 mt-2 flex-wrap" data-testid="sc-rec-row">
            {(rec_values || []).map((rv) => (
              <button key={rv} type="button" onClick={() => setRec(rv)} className={`px-3 py-2 rounded-md text-sm font-medium transition ${rec === rv ? REC_COLOR[rv] : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"}`} data-testid={`sc-rec-${rv}`}>
                {REC_LABEL[rv] || rv}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-sm">Additional notes (optional)</Label>
          <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="sc-notes" />
        </div>

        <div className="flex justify-end pt-2">
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="sc-submit">
            {busy ? "Submitting…" : "Submit scorecard"}
          </Button>
        </div>
      </Card>
    </ScorecardShell>
  );
}

function QuestionField({ q, value, onChange }) {
  return (
    <div data-testid={`sc-q-${q.id}`}>
      <Label className="text-sm font-medium">
        {q.label} {q.required && <span className="text-rose-600">*</span>}
      </Label>
      <div className="mt-1.5">
        {q.type === "rating_5" && <Stars value={value || 0} onChange={onChange} />}
        {q.type === "yes_no" && (
          <div className="flex gap-2">
            {["Yes", "No"].map((opt) => (
              <button key={opt} type="button" onClick={() => onChange(opt)} className={`px-3 py-1.5 rounded-md text-sm border ${value === opt ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"}`}>
                {opt}
              </button>
            ))}
          </div>
        )}
        {q.type === "text" && <Textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} />}
        {q.type === "single_select" && (
          <Select value={value || ""} onValueChange={onChange}>
            <SelectTrigger><SelectValue placeholder="Pick one" /></SelectTrigger>
            <SelectContent>
              {(q.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {q.type === "multi_select" && (
          <div className="flex flex-wrap gap-1.5">
            {(q.options || []).map((o) => {
              const arr = Array.isArray(value) ? value : [];
              const active = arr.includes(o);
              return (
                <button key={o} type="button" onClick={() => onChange(active ? arr.filter((x) => x !== o) : [...arr, o])}
                        className={`px-2.5 py-1 rounded-full text-xs border ${active ? "bg-indigo-100 border-indigo-400 text-indigo-900" : "bg-white border-zinc-300 text-zinc-700 hover:bg-zinc-50"}`}>
                  {active ? "✓ " : ""}{o}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function ScorecardShell({ children }) {
  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-2">
          <div className="w-7 h-7 rounded bg-indigo-600 text-white flex items-center justify-center text-sm font-bold">S</div>
          <div>
            <div className="text-sm font-semibold text-zinc-900">SaarOne</div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-400">Interview scorecard</div>
          </div>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-4 py-6 space-y-5">{children}</main>
      <footer className="text-center text-xs text-zinc-400 py-8">Powered by SaarOne</footer>
    </div>
  );
}
